import { Alert, Container, Grid, Group, Loader, Stack } from '@mantine/core';
import { AlertCircle, AlertTriangle } from 'react-feather';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FullContentHeader } from '../../components/layout/FullContentHeader';
import { SessionCompleteBadge } from '../../components/session/SessionCompleteBadge';
import { useFeatureFlag } from '../../stores/featureFlags';
import { useAuthStore } from '../../stores/auth';
import { useCoachPostSession } from '../../hooks/useCoachPostSession';
import type { PostSessionBundle } from '../../services/api/coachPostSession';
import { useRecapDraft } from '../../hooks/useRecapDraft';
import AttendanceEngagementCard from './components/post-session/AttendanceEngagementCard';
import SessionNotesCard from './components/post-session/SessionNotesCard';
import LogCommitmentsCard from './components/post-session/LogCommitmentsCard';
import MedleyFeedbackFormCard from './components/post-session/MedleyFeedbackFormCard';
import RecapEmailDraftCard from './components/post-session/RecapEmailDraftCard';
import AirtimeBalanceCard from './components/post-session/AirtimeBalanceCard';
import DeleteTranscriptControl from './components/post-session/DeleteTranscriptControl';
import SessionThemesCard from './components/post-session/SessionThemesCard';
import InSessionInsightsCard from './components/post-session/InSessionInsightsCard';

/**
 * MED-794 / Story 2.6a — Coach Post-Session screen.
 *
 * Route: `/coach/cohorts/:cohortId/sessions/:sessionId/post-session`
 *
 * Two-column layout matching the Jira screenshots (image-20260514-193924.png
 * and image-20260514-193904.png):
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Header: ← {cohortName}  · Session Complete pill             │
 *   │ Meta:   {program} · {organization} · {duration}              │
 *   │ ┌─────────────────────────────────────┬───────────────────┐ │
 *   │ │ Attendance & Engagement              │ Recap Email Draft │ │
 *   │ │ Your Session Notes                   │ Medley Feedback   │ │
 *   │ │ Log Commitments                      │   Form            │ │
 *   │ └─────────────────────────────────────┴───────────────────┘ │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Gated by the `live_session_delivery` feature flag (MED-886). Anything
 * out-of-scope this story (AI body gen, AI Clean Up, AI follow-up
 * scheduling, recap send) is documented in stories/MED-794.md.
 */
export interface CoachPostSessionPageProps {
  /**
   * MED-1040 — DEV-only static preview: seeds the bundle and bypasses the
   * feature-flag gate + fetch so the full page renders from a fixture
   * (`/dev/post-session/coach`). Compiled out of production builds.
   */
  preview?: { bundle: PostSessionBundle };
}

export function CoachPostSessionPage({
  preview,
}: CoachPostSessionPageProps = {}) {
  const isPreview = !!preview;
  const navigate = useNavigate();
  const params = useParams<{ cohortId: string; sessionId: string }>();
  const cohortId = params.cohortId ?? preview?.bundle.session.cohort_id;
  const sessionId = params.sessionId ?? preview?.bundle.session.id;
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');
  const currentUser = useAuthStore(s => s.user);

  const {
    bundle,
    loading,
    error,
    refresh,
    attendance,
    setAttendanceForParticipant,
    saveAttendance,
    savingAttendance,
    feedbackResponses,
    setFeedbackResponse,
    saveFeedbackDraft,
    submitFeedback,
    savingFeedback,
    feedbackSubmittedAt,
    concernFlags,
    setConcernFlags,
    commitments,
    saveCommitment,
    savingParticipantId,
    commitmentNotices,
  } = useCoachPostSession(sessionId ?? null, {
    previewBundle: preview?.bundle,
  });

  // MED-893 AC7/AC8 — pre-populate Recap Email body and Medley Feedback Form
  // `session_insights` (re-targeted from `session_themes` in MED-988) +
  // `carryover_for_next_session` from the AI recap draft.
  // Hook returns `{ result: null }` on fail_open / 503; downstream cards check
  // `envelope.result` before pre-populating.
  //
  // MED-971 — also gate the AI fetch on the cohort's `ai_features_enabled` flag
  // from the bundle. When AI is off for the cohort, the template seeds the
  // body instead (AC1) — issuing the AI request would be a wasted round-trip.
  const aiFeaturesEnabled = bundle?.ai_features_enabled === true;
  // MED-1128 — post-session AI (recap) requires BOTH modality AI AND the session's
  // listening state: AI off for the session means no transcript, so an AI recap
  // would be ungrounded. When listening was off, treat the recap exactly like the
  // modality-off case (template, no AI seed) and surface an inline note.
  const aiListeningEnabled = bundle?.ai_listening_enabled === true;
  const recapAiAvailable = aiFeaturesEnabled && aiListeningEnabled;
  const aiListeningOffForSession = aiFeaturesEnabled && !aiListeningEnabled;
  const { envelope: recapDraftEnvelope } = useRecapDraft(
    sessionId ?? null,
    liveSessionDeliveryEnabled && recapAiAvailable
  );

  if (!sessionId || !cohortId) {
    return <Navigate to="/coach" replace />;
  }

  // Render the "feature off" alert inline rather than redirecting. The
  // `useFeatureFlag` hook returns `false` until the async fetch completes,
  // so an unconditional redirect-on-false would fire on first render and
  // block the page even when the flag is actually on. Matches the
  // CoachActiveSessionPage pattern.
  // MED-1181: also block when the cohort's per-modality LSD toggle is off on the
  // loaded bundle (defense-in-depth). The flag lives on the nested session
  // (`SessionWithDetails.live_session_delivery_enabled`). Null bundle (still
  // loading) does not trip the modality half.
  if (
    !isPreview &&
    (!liveSessionDeliveryEnabled ||
      bundle?.session.live_session_delivery_enabled === false)
  ) {
    return (
      <Container size="md" py="xl">
        <Alert
          color="warningAmber"
          icon={<AlertTriangle size={16} />}
          title="This feature isn't available yet"
        >
          The live session post-session experience is gated by a feature flag
          that is currently off in this environment.
        </Alert>
      </Container>
    );
  }

  if (loading && !bundle) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center">
          <Loader />
        </Group>
      </Container>
    );
  }

  if (error || !bundle) {
    return (
      <Container size="xl" py="xl">
        <Alert
          color="red"
          icon={<AlertCircle size={16} />}
          title="Couldn't load post-session screen"
        >
          {error ?? 'Unknown error'}
        </Alert>
      </Container>
    );
  }

  const session = bundle.session;
  const participants = bundle.participants;
  const groupName = session.group_name;
  const cohortName = session.cohort_name ?? 'Cohort';
  const programName = session.cohort_code;
  // MED-901: derive the merge-tag context the Send Recap Email modal needs.
  // The bundle's PostSessionSessionWithDetails carries everything except
  // coach_name, which we read from the auth store (the coach is, by definition,
  // the authenticated user on this screen).
  const coachName = currentUser?.display_name ?? '';
  const sessionDateIso = session.scheduled_start ?? null;
  const topicName = session.topic_name ?? '';

  return (
    <>
      <FullContentHeader
        data-testid="post-session-header"
        title={cohortName}
        programName={programName}
        clientName={groupName}
        durationMinutes={session.duration_minutes}
        onBack={() => navigate('/coach')}
        sticky
        accessory={<SessionCompleteBadge />}
      />
      <Container size="xl" py="lg">
        <Stack gap="lg">
          <Grid gutter="lg">
            {/* Left column — wider (8/12) */}
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Stack gap="md">
                <AttendanceEngagementCard
                  participants={participants}
                  attendance={attendance}
                  onParticipantChange={setAttendanceForParticipant}
                  onSave={saveAttendance}
                  saving={savingAttendance}
                />
                {/* MED-1035 Part A: Medley Feedback Form moved into the primary
                  (left) column, swapping positions with Session Notes. */}
                <MedleyFeedbackFormCard
                  responses={feedbackResponses}
                  onChange={setFeedbackResponse}
                  onSaveDraft={saveFeedbackDraft}
                  onSubmit={submitFeedback}
                  saving={savingFeedback}
                  submittedAt={feedbackSubmittedAt}
                  recapDraftEnvelope={recapDraftEnvelope}
                  participants={participants}
                  concernFlags={concernFlags}
                  onConcernFlagsChange={setConcernFlags}
                  canFlagConcerns={currentUser?.coach_type !== 'partner'}
                />
                <LogCommitmentsCard
                  participants={participants}
                  commitments={commitments}
                  onSave={saveCommitment}
                  savingParticipantId={savingParticipantId}
                  rowNotices={commitmentNotices}
                />
              </Stack>
            </Grid.Col>

            {/* Right column — narrower (4/12) */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="md">
                <RecapEmailDraftCard
                  sessionId={sessionId}
                  participants={participants}
                  groupName={groupName ?? null}
                  cohortName={cohortName}
                  sessionNumber={session.session_number}
                  sessionDateIso={sessionDateIso}
                  topicName={topicName}
                  coachName={coachName}
                  recapDraftEnvelope={recapDraftEnvelope}
                  nextSessionDateIso={bundle.next_session_date}
                  defaultTemplate={bundle.recap_email_default_template}
                  aiFeaturesEnabled={recapAiAvailable}
                  aiListeningOffForSession={aiListeningOffForSession}
                  savedDraftEdit={bundle.recap_draft_edit}
                />
                {/* MED-894 (B7) — Airtime Balance: the aggregate speaking-time
                  split, visual only (interpretation text is B10's card). Rides
                  the bundle envelope; absent when AI wasn't active. */}
                <AirtimeBalanceCard
                  airtime={bundle.airtime}
                  aiAvailable={recapAiAvailable}
                  loading={false}
                  viewerRole="coach"
                />
                {/* MED-1194 (B10) — in-session insights (airtime interpretation
                  copy), between Airtime Balance and Discussion Themes (AC7). */}
                <InSessionInsightsCard
                  insights={bundle.insession_insights}
                  aiAvailable={recapAiAvailable}
                />
                {/* MED-1193 (B9) — discussion themes, coach register, below
                  the Airtime Balance card (confirmed placement 7/13). */}
                <SessionThemesCard
                  themes={bundle.themes}
                  aiAvailable={recapAiAvailable}
                  viewerRegister="coach"
                />
                {/* MED-1187 (AC8) — manual "delete for sure" for the stored
                  transcript; hidden when none exists. */}
                <DeleteTranscriptControl
                  sessionId={sessionId}
                  hasTranscript={bundle.has_transcript}
                />
                {/* MED-1035 Part A: Session Notes moved into the secondary (right)
                  column, swapping positions with the Medley Feedback Form. */}
                <SessionNotesCard
                  sessionId={sessionId}
                  initialHtml={session.coach_session_notes_html ?? ''}
                  initialUpdatedAt={session.coach_session_notes_updated_at}
                  onSaved={refresh}
                />
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>
    </>
  );
}

export default CoachPostSessionPage;
